import React from 'react';
import styled from 'styled-components';
import { Box, Button, Flex, FlexColumn, Text } from 'core/components';
import { Field, TextArea } from 'core/form/components';
import { getModifierSymbol } from 'core/util/platform';
import CommentAvatar from './CommentAvatar';

const PLACEHOLDER = 'Add a comment or paste a ticket link';

const Prompt = styled(Flex)`
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

/** AddEditComment's thread look: a collapsed prompt that opens into the comment box. Must render inside its form. */
const CommentThreadComposer = ({
  userName,
  editMode,
  open,
  saving,
  commentIsEmpty,
  dense,
  divider,
  onOpen,
  onCancel,
  onSubmit
}) => {
  const submitDisabled = commentIsEmpty || saving;

  const handleTextKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && !submitDisabled) {
      onSubmit();
    }
  };

  const handlePromptKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  };

  return (
    <Box pt={divider ? 2 : 0} mt={divider || editMode ? 1 : 2} borderTop={divider ? 'thin' : undefined}>
      <Flex alignItems="flex-start" gap={dense ? '9px' : '12px'}>
        {!editMode && <CommentAvatar name={userName} />}
        <Box flex="1 1 auto" minWidth={0}>
          {editMode || open ? (
            <FlexColumn gap={1}>
              <Field name="comment" showLabel={false} placeholder={PLACEHOLDER} mb={0} autoFocus>
                <TextArea
                  fill
                  autoGrow
                  resize="vertical"
                  rows={dense ? 2 : 3}
                  style={{ maxHeight: dense ? 160 : 320 }}
                  onKeyDown={handleTextKeyDown}
                />
              </Field>
              <Flex gap={1} justifyContent="flex-end" alignItems="center">
                <Text small muted>
                  {getModifierSymbol()}↵ to {editMode ? 'save' : 'comment'}
                </Text>
                <Button small minimal text="Cancel" disabled={saving} onClick={onCancel} />
                <Button
                  small
                  intent="primary"
                  text={editMode ? 'Save' : 'Comment'}
                  loading={saving}
                  disabled={submitDisabled}
                  onClick={onSubmit}
                />
              </Flex>
            </FlexColumn>
          ) : (
            <Prompt
              role="button"
              tabIndex={0}
              px="9px"
              py="6px"
              border="thin"
              borderRadius={3}
              cursor="text"
              onClick={onOpen}
              onKeyDown={handlePromptKeyDown}
            >
              <Text small muted>
                {PLACEHOLDER}
              </Text>
            </Prompt>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default CommentThreadComposer;
